import { useMemo, Fragment } from 'react';
import { useTable, useSortBy, useExpanded } from 'react-table';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useQuery, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import ThemeLink from '../../components/ThemeLink';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import ActionsDropdown from '../../components/ActionsDropdown';
import { formatDateCellShort } from '../../helpers/reactTable';
import { ORDERS_QUERY } from '../../queries';
import { AUDIENCE_QUERY } from '../../queries/audienceQueries';
import AsyncFiltersDisplay from '../../components/AsyncFiltersDisplay';
import Button from '../../components/inputs/Button';
import { capitalize, currency } from '../../utils';


function OrdersTable({ data }) {
  const navigate = useNavigate();
  const client = useApolloClient();

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        id: 'name',
        Cell: ({ row }) =>
          row.original.audienceName ||
          `${row.original.seedFileName} (${row.original.packageName})`,
      },
      {
        Header: 'Order Date',
        accessor: 'createdDate',
        Cell: ({ value }) => formatDateCellShort({ value: Number(value) }),
      },
      {
        Header: 'Order Type',
        accessor: 'orderType',
        Cell: ({ value }) => capitalize(value),
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => {
          const handleEnhance = async () => {
            try {
              const { data } = await client.query({
                query: AUDIENCE_QUERY,
                variables: { audienceName: row.original.audienceName },
                fetchPolicy: 'network-only',
              });

              if (!data?.audience) {
                alert('Audience not found');
                return;
              }

              if (!data.audience.purchasedAt) {
                alert('This audience has not been purchased yet and cannot be enhanced');
                return;
              }

              navigate(`../../enhancements/order/audience`, {
                state: {
                  orderDetails: {
                    coreProductName: 'audience',
                    audienceId: data.audience.id,
                    audienceName: row.original.audienceName,
                    recordsIncluded: row.original.recordsIncluded,
                  },
                },
              });
            } catch (error) {
              console.error('Error fetching audience:', error);
              alert('Failed to load audience data');
            }
          };

          const handleProfile = async () => {
            try {
              const { data } = await client.query({
                query: AUDIENCE_QUERY,
                variables: { audienceName: row.original.audienceName },
                fetchPolicy: 'network-only',
              });

              if (!data?.audience) {
                alert('Audience not found');
                return;
              }

              if (!data.audience.purchasedAt) {
                alert('This audience has not been purchased yet and cannot be profiled');
                return;
              }

              navigate(`../../insights/audience/${data.audience.id}`);
            } catch (error) {
              console.error('Error fetching audience:', error);
              alert('Failed to load audience data');
            }
          };

          return (
            <ActionsDropdown
              row={row}
              orderType={row.original.orderType}
              audienceName={row.original.audienceName}
              showEnhance={row.original.orderType === 'audience'}
              showProfile={row.original.orderType === 'audience'}
              onEnhance={handleEnhance}
              onProfile={handleProfile}
            />
          );
        },
      },
      {
        Header: 'Price',
        accessor: 'amount',
        Cell: ({ value }) => currency(value),
      },
      {
        Header: 'Receipt',
        accessor: 'id',
        Cell: ({ value, row }: { value: string; row: any }) => {
          // Map orderType to the correct route path
          let routePath: string;
          switch (row.original.orderType) {
            case 'audience-enhancement':
              routePath = 'enhancements';
              break;
            case 'enhancement':
              routePath = 'enhancements';
              break;
            case 'insight':
              routePath = 'insights';
              break;
            case 'audience':
              routePath = 'audiences';
              break;
            case 'audience-profile':
              routePath = 'insights/audience';
              break;
            default:
              routePath = row.original.orderType;
          }

          return (
            <ThemeLink to={`../../${routePath}/order/${value}`}>
              View
            </ThemeLink>
          );
        },
      },
      {
        Header: 'Details',
        id: 'details',
        Cell: ({ row }) => {
          const orderType = row.original.orderType;
          return (
            <div className="">
              {orderType === 'audience' && (
                <span
                  className="font-bold text-blue hover:text-primary"
                  {...row.getToggleRowExpandedProps()}
                >
                  {row.isExpanded
                    ? 'Hide Audience Details'
                    : 'Show Audience Details'}
                </span>
              )}
              {orderType === 'enhancement' && (
                <p>
                  <span className="font-bold">Package:</span>{' '}
                  {row.original.packageName}
                </p>
              )}
              {orderType === 'insight' && (
                <p>
                  <span className="font-bold">Package:</span>{' '}
                  {row.original.packageName}
                </p>
              )}
            </div>
          );
        },
      },
    ],
    [navigate]
  );


  const initialSortBy = useMemo(() => [{ id: 'createdDate', desc: true }], []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: initialSortBy,
      },
    },
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = tableInstance;

  return (
    <div className="h-full w-full overflow-auto pb-52">
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <Fragment key={row.original.id}>
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td className="p-2" {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      <div className="pt-2 px-14 pb-6 border-t w-full">
                        <p className="px-6 pt-3 text-lg">
                          <span className="font-bold">Records Purchased:</span>{' '}
                          {Number(row.original.recordsIncluded || 0).toLocaleString()}
                        </p>
                        <div className="h-px bg-gray-200 mt-3 w-1/2"></div>
                        <AsyncFiltersDisplay
                          audienceName={row.original.audienceName}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">
          You haven't made any orders yet. You can build and buy audiences in
          the{' '}
          <ThemeLink to="../../audiences/audiencebuilder">
            Audience Builder.
          </ThemeLink>
        </p>
      )}
    </div>
  );
}

export default function Orders() {
  const { loading, error, data } = useQuery(ORDERS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <div className="w-full">
      {loading && (
        <LoadingSpinner message="Getting your orders..." className="mt-10" />
      )}
      {!loading && error && <ErrorText message={error.message} />}
      {!loading && !error && data && <OrdersTable data={data.orders} />}
    </div>
  );
}
